/**
 * Date picker block — Popover + Calendar with dropdown month/year (shadcn Date of Birth pattern).
 *
 * For date-only selection (e.g. date of birth). Value is "YYYY-MM-DD".
 * Uses captionLayout="dropdown" and closes the popover on select.
 *
 * @module components/blocks/date-picker
 */

import { Button } from '@ui/components/ui/button';
import { Calendar } from '@ui/components/ui/calendar';
import { Field, FieldLabel } from '@ui/components/ui/field';
import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/ui/popover';
import * as React from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DatePickerProps {
	/** Current value: "YYYY-MM-DD" or "". */
	value: string;
	/** Called when date changes. */
	onChange: (value: string) => void;
	/** Label for the field (e.g. "Date of birth"). */
	label?: React.ReactNode;
	/** Placeholder when no date selected. */
	placeholder?: string;
	id?: string;
	disabled?: boolean;
	/** Min selectable date (calendar fromDate). */
	fromDate?: Date;
	/** Max selectable date (calendar toDate). */
	toDate?: Date;
	/** Applied to the Field wrapper. */
	className?: string;
	/** Button variant. */
	variant?: React.ComponentProps<typeof Button>['variant'];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Parse "YYYY-MM-DD" to local Date (midnight local time). */
function parseDate(value: string): Date | undefined {
	if (!value || value.length < 10) return undefined;
	const [y, m, d] = value.split('-').map(Number);
	if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return undefined;
	const date = new Date(y, m - 1, d);
	if (Number.isNaN(date.getTime())) return undefined;
	return date;
}

/** Format Date to "YYYY-MM-DD". */
function formatDate(date: Date): string {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, '0');
	const d = String(date.getDate()).padStart(2, '0');
	return `${y}-${m}-${d}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DatePicker({
	value,
	onChange,
	label = 'Date',
	placeholder = 'Select date',
	id,
	disabled,
	fromDate,
	toDate,
	className,
	variant = 'outline',
}: DatePickerProps) {
	const [open, setOpen] = React.useState(false);
	const date = parseDate(value);

	const handleSelect = React.useCallback(
		(selected: Date | undefined) => {
			if (!selected) {
				onChange('');
				setOpen(false);
				return;
			}
			onChange(formatDate(selected));
			setOpen(false);
		},
		[onChange],
	);

	const triggerId = id ?? 'date-picker';

	return (
		<Field className={className}>
			<FieldLabel htmlFor={triggerId}>{label}</FieldLabel>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger
					render={
						<Button variant={variant} disabled={disabled} id={triggerId} className="justify-start font-normal">
							{date ? date.toLocaleDateString() : placeholder}
						</Button>
					}
				/>
				<PopoverContent className="w-auto overflow-hidden p-0" align="start">
					<Calendar
						mode="single"
						selected={date}
						defaultMonth={date}
						captionLayout="dropdown"
						fromDate={fromDate}
						toDate={toDate}
						onSelect={handleSelect}
					/>
				</PopoverContent>
			</Popover>
		</Field>
	);
}
